import type { ActionContext } from "vuex";
import type { RootState } from "@/store/types";
import type { TracesState } from "@/store/traces/state";
import type { DateTime } from "luxon";
import type { GetFilteredTracesV2 } from "@/models/traces";
import type { DateHour, Filter } from "@/models/common";
import type { GotFilenames } from "@/models/api/getFilenames.ts";
import dateHoursFromRange from "@/utils/dateHoursFromRange";
import perspective from "@/perspective";
import getFilenames from "@/api/getFilenames";
import executeTracesQueryV2 from "@/store/traces/actions/executeTracesQuery.ts";
import { QueryData } from "@/warehouse/QueryData";
import { RandomSelection } from "@/algorithms/loadingStrategies";
import {
  getFilenamesPayloadsFromDateHours,
  reduceGetFilenamesResponses,
} from "@/utils/getFilenamesUtils";
import { dropTable } from "@/duckDB";

/**
 * Function in charge of querying the Traces data.
 * @param context - Traces Vuex module context.
 * @param args - Arguments object containing the required parameters.
 * @param args.startTime - Start DateTime for the Traces to be fetched.
 * @param args.endTime - End DateTime for the Traces to be fetched.
 * @param args.filters - Object with string and number filters for the Traces.
 * @param args.shouldOverride - Flag that indicates that the user has applied a new set of dates and/or filters.
 * @param args.fetchLimitIncreaseValue - Numeric value that indicates the amount of traces to increase to the fetchLimit value.
 * @param args.testParallelQueriesMax - Debug parameter used for testing the max amount of parallel queries to be run.
 * @returns Void or an object with the error message is something happened.
 */
export async function getTraces(
  context: ActionContext<TracesState, RootState>,
  args: {
    startTime: DateTime;
    endTime: DateTime;
    filters?: Filter[];
    shouldOverride?: boolean;
    fetchLimitIncreaseValue: number;
    testParallelQueriesMax?: number;
  }
): Promise<undefined | { success: boolean; message: string }> {
  const PARALLEL_QUERIES_MAX = args.testParallelQueriesMax || 1;
  try {
    if (context.getters["getStreamingIsPaused"]) return;

    context.commit("setLoading", true);

    // If not a new set of filters and there are objects stored available
    if (
      !args.shouldOverride &&
      QueryData.getQueryData("traces") !== undefined
    ) {
      // Resume execution of the previous queries
      const resolve = await runParallelQueries(
        context,
        context.getters["getLatestQueryStartedAt"],
        PARALLEL_QUERIES_MAX,
        args.fetchLimitIncreaseValue
      );

      // Return if an error happened
      if (resolve) {
        // Reset variables is there's an error
        if (!resolve.success) await resetStateVariables(context);

        return {
          success: resolve.success,
          message: resolve.message,
        };
      }

      return;
    }

    // Reset previous stored values
    QueryData.deleteQueryData("traces");
    context.commit("setFetchLimit", args.fetchLimitIncreaseValue);
    context.commit("setDataProcessed", 0);
    context.commit("setDataReturned", 0);
    await dropTable("traces");

    // Transform start and end times to DateHours
    const allDateHours: DateHour[] = dateHoursFromRange(
      args.startTime,
      args.endTime
    );

    const getFilenamesPayloads = getFilenamesPayloadsFromDateHours(
      allDateHours,
      "TRACES"
    );
    const getFilenamesResults = await Promise.all(
      getFilenamesPayloads.map((payload) => getFilenames(payload))
    );
    const getFilenameErrors = getFilenamesResults.filter(
      (resp) => !resp.results
    );
    if (getFilenameErrors.length > 0) {
      await resetStateVariables(context);
      const message = getFilenameErrors[0].message as string;
      return {
        success: false,
        message: message,
      };
    }

    const reducedGetFilenames =
      reduceGetFilenamesResponses(getFilenamesResults);
    const filenameResults = reducedGetFilenames.results as GotFilenames;
    context.commit("setHasReportedTraces", filenameResults.hasReportedData);
    const files = filenameResults.files;
    if (files.length == 0) {
      context.commit("setShouldExecuteQuery", false);
      QueryData.deleteQueryData("traces");
      QueryData.completeProgressValue("traces");
      await dropTable("traces");
      context.commit("setLoading", false);
      context.commit("setTracesCount", 0);
      context.commit("setShouldShowTable", false);
      perspective.clearTable("traces");
      return {
        success: true,
        message: "No data found for this time range",
      };
    }

    const sharedQueryInputParam: GetFilteredTracesV2 = {
      filename: "",
      startTime: args.startTime.toMillis(),
      endTime: args.endTime.toMillis(),
      filters: args.filters,
      pageSize: 2000,
    };
    QueryData.setQueryData("traces", sharedQueryInputParam, files);

    // Get the queryParams from Vuex action to update the flags and run them
    const resolve = await runParallelQueries(
      context,
      context.getters["getLatestQueryStartedAt"],
      PARALLEL_QUERIES_MAX,
      args.fetchLimitIncreaseValue
    );

    if (resolve) {
      // Reset variables is there's an error
      if (!resolve.success) await resetStateVariables(context);

      return {
        success: resolve.success,
        message: resolve.message,
      };
    }
  } catch (e: any) {
    const error = e.graphQLErrors ? e.graphQLErrors[0] : e;
    return {
      success: false,
      message: error.message,
    };
  }
}

/**
 * Function that encapsulates the functionality of fetching the queryParams and running the queries.
 * @param context - Traces Vuex module context.
 * @param startedAtTime - Time at which the queries started to run
 * @param PARALLEL_QUERIES_MAX - Defines the max amount of parallel queries to be run at the time.
 * @param fetchLimitIncreaseValue - Numeric value that indicates the amount of traces to increase to the fetchLimit value.
 * @returns void or an object with the first error message found.
 */
async function runParallelQueries(
  context: ActionContext<TracesState, RootState>,
  startedAtTime: DateTime,
  PARALLEL_QUERIES_MAX: number,
  fetchLimitIncreaseValue: number
): Promise<void | { success: boolean; message: string }> {
  const loadingStrategy = new RandomSelection();
  const storedQueryParams = loadingStrategy.getQueryInputs(
    "traces",
    PARALLEL_QUERIES_MAX,
    false
  ) as GetFilteredTracesV2[];
  const queryPromises: Promise<void | { message: string }>[] =
    storedQueryParams.map((queryParams) =>
      executeTracesQueryV2(context, queryParams, startedAtTime)
    );

  const errors = (await Promise.all(queryPromises)).filter(
    (x) => x !== undefined
  );
  context.commit("incUpdateTraces");

  // We don't reset queryParams states as we could have returned from the functions on view change
  context.commit("setLoading", false);

  // Get ready for when 'Resume' button is clicked
  if (context.getters["getTracesCount"] >= context.getters["getFetchLimit"]) {
    context.commit(
      "setFetchLimit",
      context.getters["getFetchLimit"] + fetchLimitIncreaseValue
    );
    context.commit("setStreamingIsPaused", true);
  }

  if (errors.length > 0) {
    return {
      success: false,
      message: (errors[0] as { message: string }).message,
    };
  }

  // Checks only for the resulting calls that are currently active
  if (
    context.getters["getTracesCount"] === 0 &&
    startedAtTime >= context.getters["getLatestQueryStartedAt"] &&
    context.getters["getShouldExecuteQuery"]
  ) {
    return {
      success: true,
      message: "No data found for this time range",
    };
  }
}

/**
 * Function that resets the Traces Vuex State variables when an error occurs.
 * @param context - Traces Vuex module context.
 */
async function resetStateVariables(
  context: ActionContext<TracesState, RootState>
) {
  QueryData.deleteQueryData("traces");
  context.commit("setLoading", false);
  context.commit("setShouldExecuteQuery", false);
  await dropTable("traces");
}
